"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { RiMedicineBottleLine } from "react-icons/ri";
import dynamic from "next/dynamic";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import { pharmacyAddressText } from "@/components/dosespot/pharmacy-search-select";
import { Tooltip } from "@/components/ui";
import Button from "@/components/ui/button";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { Skeleton } from "@/components/ui/skeleton";
import { useLaunchDoseSpot } from "@/hooks/use-launch-dosespot";
import { useModule } from "@/hooks/use-module";
import { formatPhoneNumber } from "@/lib/format-data";
import type { PatientPharmacyEntry } from "@/types/dosespot";
import type { FormTable } from "@/types/forms/form";
import type { PatientChart } from "@/types/patient";
import type { UserTiny } from "@/types/user";
import AddPharmacyDialog from "./add-pharmacy-dialog";
import { usePatientPharmaciesQuery, useRemovePatientPharmacyMutation, useSyncReportedPharmacyMutation } from "@/query/patient/pharmacies";

// Lazy-loaded: the submissions drawer pulls in @react-pdf, which we only want to load
// when a provider actually opens a submission (and keeps it out of the import graph).
const SubmissionsDrawer = dynamic(() => import("@/components/form/submissions-drawer"), {
  ssr: false
});
interface PharmaciesSectionProps {
  patient: PatientChart;
  forms?: FormTable[];
}
interface OpenSubmission {
  form: FormTable;
  answeredFormId?: string;
}
export default function PharmaciesSection(t0) {
  const $ = _c(39);
  const {
    patient,
    forms
  } = t0;
  const hasDoseSpot = useModule("DOSESPOT");
  let t1;
  if ($[0] !== patient) {
    t1 = {
      patient
    };
    $[0] = patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    dosespotLocations
  } = useLaunchDoseSpot(t1);
  const enabled = hasDoseSpot && dosespotLocations.length > 0;
  const {
    data,
    isLoading,
    isError
  } = usePatientPharmaciesQuery(patient.id, enabled);
  const syncMutation = useSyncReportedPharmacyMutation(patient.id);
  const removeMutation = useRemovePatientPharmacyMutation(patient.id);
  const [openSubmission, setOpenSubmission] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [pharmacyToRemove, setPharmacyToRemove] = useState(null);
  if (!enabled) {
    return null;
  }
  const eprescribeAvailable = data?.eprescribeAvailable ?? false;
  let t2;
  if ($[2] !== data?.pharmacies) {
    t2 = data?.pharmacies ?? [];
    $[2] = data?.pharmacies;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const pharmacies = t2;
  let t3;
  if ($[4] !== pharmacies) {
    t3 = pharmacies.some(_temp);
    $[4] = pharmacies;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasPrimary = t3;
  let t4;
  if ($[6] !== eprescribeAvailable || $[7] !== syncMutation) {
    t4 = entry_0 => {
      if (!eprescribeAvailable) {
        return <Tooltip content="E-prescribing couldn't be reached, so the sync status is unavailable."><span className="cursor-default text-caption text-muted-foreground">Unknown</span></Tooltip>;
      }
      if (entry_0.inDosespot) {
        return <span className="whitespace-nowrap text-caption text-muted-foreground">In e-prescribing<span>{entry_0.isDefault ? " \xB7 Primary" : ""}</span></span>;
      }
      if (entry_0.reported && entry_0.answerId) {
        const answerId = entry_0.answerId;
        const isSyncing = syncMutation.isPending && syncMutation.variables === answerId;
        return <Button variant="secondary" size="sm" icon={FaSyncAlt} disabled={syncMutation.isPending} isLoading={isSyncing} loadingText="Syncing..." onClick={() => syncMutation.mutate(answerId)}>Sync</Button>;
      }
      return null;
    };
    $[6] = eprescribeAvailable;
    $[7] = syncMutation;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const renderStatus = t4;
  let t5;
  if ($[9] !== forms) {
    t5 = entry_1 => {
      if (!entry_1.reported) {
        return null;
      }
      const form = entry_1.formId ? forms?.find(candidate => candidate.id === entry_1.formId) : undefined;
      return <div className="text-caption text-muted-foreground">Patient reported{entry_1.formName && form && <>{" \xB7 "}<button type="button" className="text-link hover:text-link-hover hover:underline" onClick={() => setOpenSubmission({
            form,
            answeredFormId: entry_1.answeredFormId ?? undefined
          })}>{entry_1.formName}</button></>}{entry_1.formName && !form && <> · {entry_1.formName}</>}</div>;
    };
    $[9] = forms;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const renderReportedLine = t5;
  const t6 = !eprescribeAvailable;
  const t7 = eprescribeAvailable ? undefined : "E-prescribing couldn't be reached, so pharmacies can't be changed.";
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsAdding(true);
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t7) {
    t9 = <Button variant="secondary" size="sm" icon={FiPlus} disabled={t6} tooltip={t7} onClick={t8}>Add</Button>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== eprescribeAvailable || $[16] !== isError || $[17] !== isLoading || $[18] !== pharmacies || $[19] !== removeMutation || $[20] !== renderReportedLine || $[21] !== renderStatus) {
    t10 = isLoading ? <div className="divide-y divide-border">{[0, 1].map(_temp2)}</div> : isError ? <div className="py-8 text-center"><RiMedicineBottleLine className="mx-auto h-8 w-8 text-foreground-muted" /><p className="mt-2 text-body text-muted-foreground">Couldn't load pharmacies. Please try again later.</p></div> : pharmacies.length === 0 ? <div className="py-8 text-center"><RiMedicineBottleLine className="mx-auto h-8 w-8 text-foreground-muted" /><p className="mt-2 text-body text-muted-foreground">{eprescribeAvailable ? "No preferred pharmacy on file" : "E-prescribing couldn't be reached, so this patient's pharmacies can't be shown"}</p>{eprescribeAvailable && <Button variant="secondary" size="sm" icon={FiPlus} className="mt-3" onClick={() => setIsAdding(true)}>Add pharmacy</Button>}</div> : <div className="divide-y divide-border">{pharmacies.map(entry_2 => {
        const secondary = [pharmacyAddressText(entry_2.snapshot), entry_2.snapshot.primaryPhone ? formatPhoneNumber(entry_2.snapshot.primaryPhone) : null].filter(Boolean).join(" \xB7 ");
        return <div key={entry_2.dosespotPharmacyId} className="flex items-start justify-between gap-3 py-2"><div className="min-w-0"><div className="text-caption-strong text-foreground">{entry_2.snapshot.storeName}</div>{secondary && <div className="text-caption text-muted-foreground">{secondary}</div>}{renderReportedLine(entry_2)}</div><div className="flex shrink-0 items-center gap-2">{renderStatus(entry_2)}{entry_2.inDosespot && eprescribeAvailable && <Button variant="ghost" size="icon-sm" icon={FiTrash2} tooltip="Remove" aria-label={`Remove ${entry_2.snapshot.storeName}`} className="text-destructive hover:bg-status-error-bg hover:text-destructive" disabled={removeMutation.isPending} onClick={() => setPharmacyToRemove(entry_2)} />}</div></div>;
      })}</div>;
    $[15] = eprescribeAvailable;
    $[16] = isError;
    $[17] = isLoading;
    $[18] = pharmacies;
    $[19] = removeMutation;
    $[20] = renderReportedLine;
    $[21] = renderStatus;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== hasPrimary || $[24] !== isAdding || $[25] !== patient.id) {
    t11 = isAdding && <AddPharmacyDialog patientId={patient.id} onClose={() => setIsAdding(false)} defaultSetAsPrimary={!hasPrimary} />;
    $[23] = hasPrimary;
    $[24] = isAdding;
    $[25] = patient.id;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== pharmacyToRemove || $[28] !== removeMutation) {
    t12 = pharmacyToRemove && <DeleteConfirmationModal isOpen={true} setIsOpen={open => !open && setPharmacyToRemove(null)} title="Remove pharmacy" subtitle={`Remove ${pharmacyToRemove.snapshot.storeName} from this patient's e-prescribing record?${pharmacyToRemove.reported ? " It will stay on the chart as a pharmacy the patient reported on a form." : ""}`} confirmText="Remove" isLoading={removeMutation.isPending} onConfirm={async () => {
      try {
        await removeMutation.mutateAsync(pharmacyToRemove.dosespotPharmacyId);
      } catch {}
    }} />;
    $[27] = pharmacyToRemove;
    $[28] = removeMutation;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== openSubmission || $[31] !== patient) {
    t13 = openSubmission && <SubmissionsDrawer form={openSubmission.form} isOpen={!!openSubmission} onClose={() => setOpenSubmission(null)} patient={patient as UserTiny} answeredFormId={openSubmission.answeredFormId} />;
    $[30] = openSubmission;
    $[31] = patient;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t13 || $[37] !== t9) {
    t14 = <CollapsibleSection title="Pharmacies" persistKey="pharmacies" id="pharmacies-section" actions={t9}>{t10}{t11}{t12}{t13}</CollapsibleSection>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t9;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}
function _temp2(row) {
  return <div key={row} className="flex items-start justify-between gap-3 py-2"><div className="min-w-0 space-y-1.5"><Skeleton className="h-3.5 w-40" /><Skeleton className="h-3 w-56" /></div><Skeleton className="h-5 w-20" /></div>;
}
function _temp(entry) {
  return entry.isDefault;
}